import { memo } from 'react'
import { Node } from 'reactflow'
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select"
import { Switch } from "@/shared/components/ui/switch"
import { Badge } from "@/shared/components/ui/badge"
import { Textarea } from "@/shared/components/ui/textarea"
import { Loader2, Mic, PenLine, MessageSquare } from "lucide-react"
import { useVoiceConfig } from "@/modules/agent/hooks/useVoiceConfig"

interface EntryNodeConfigProps {
  selectedNode: Node
  onUpdateNodeData: (field: string, value: unknown) => void
  onOpenPromptEditor: (field: string, content: string, title: string, description: string) => void
}

export const EntryNodeConfig = memo(({ 
  selectedNode, 
  onUpdateNodeData, 
  onOpenPromptEditor 
}: EntryNodeConfigProps) => {
  // 加载语音配置
  const { asrProviders, defaultAsrProvider, isLoading: isLoadingVoiceConfig } = useVoiceConfig()
  
  return (
    <>
      <div className="space-y-2">
        <Label>入口名称</Label>
        <Input 
          value={selectedNode.data.label || ''} 
          onChange={(e) => onUpdateNodeData('label', e.target.value)}
        />
      </div>
      
      {/* V6.0: 语音输入配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mic className="h-4 w-4 text-blue-500" />
            <Label htmlFor="enable-voice-switch" className="text-sm font-medium">
              语音输入
            </Label>
          </div>
          <Switch
            id="enable-voice-switch"
            checked={selectedNode.data.enableVoice ?? false}
            onCheckedChange={(checked) => onUpdateNodeData('enableVoice', checked)}
          />
        </div>
        <p className="text-xs text-slate-500">
          开启后支持语音输入，用户可通过麦克风与 Agent 对话
        </p>
        
        {/* ASR 配置（仅当语音输入开启时显示） */}
        {selectedNode.data.enableVoice && (
          <div className="space-y-3 pt-2">
            <div className="space-y-2">
              <Label className="text-sm">语音识别服务</Label>
              {isLoadingVoiceConfig ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  加载中...
                </div>
              ) : (
                <Select
                  value={selectedNode.data.asrProvider || defaultAsrProvider || 'aliyun'}
                  onValueChange={(value) => onUpdateNodeData('asrProvider', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择 ASR 服务" />
                  </SelectTrigger>
                  <SelectContent>
                    {asrProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        <div className="flex items-center gap-2">
                          <span>{provider.name}</span>
                          {provider.default && (
                            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                              默认
                            </Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            {/* 自定义 ASR URL（当 custom 或 pet 时显示） */}
            {(selectedNode.data.asrProvider === 'custom' || selectedNode.data.asrProvider === 'pet') && (
              <div className="space-y-2">
                <Label className="text-sm">
                  {selectedNode.data.asrProvider === 'pet' ? 'Pet ASR URL' : '自定义 ASR URL'}
                </Label>
                <Input
                  value={selectedNode.data.customAsrUrl || ''}
                  onChange={(e) => onUpdateNodeData('customAsrUrl', e.target.value)}
                  placeholder={selectedNode.data.asrProvider === 'pet' 
                    ? 'https://your-pet-asr-server.com/v1/asr' 
                    : 'https://your-asr-server.com/v1/speech-to-text'}
                />
                <p className="text-xs text-slate-500">
                  {selectedNode.data.asrProvider === 'pet' 
                    ? 'Pet ASR 服务的完整 URL 地址' 
                    : '自定义语音识别服务的 API 地址'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>


      {/* V6.0: 用户提示词配置 */}
      <div className="pt-3 mt-3 border-t space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-green-500" />
            <Label htmlFor="enable-user-prompt-switch" className="text-sm font-medium">
              用户提示词
            </Label>
          </div>
          <Switch
            id="enable-user-prompt-switch"
            checked={selectedNode.data.enable_user_prompt ?? false}
            onCheckedChange={(checked) => onUpdateNodeData('enable_user_prompt', checked)}
          />
        </div>
        <p className="text-xs text-slate-500">
          开启后可为不同触发类型配置专用的用户提示词模板
        </p>

        {/* User Prompt 模板配置（仅当开启时显示） */}
        {selectedNode.data.enable_user_prompt && (
          <div className="space-y-3 pt-2">
            <Label className="text-sm font-medium">User Prompt 模板</Label>
            
            {/* 用户输入模板 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-600">用户输入 (user)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onOpenPromptEditor(
                    'user_prompt_templates.user',
                    selectedNode.data.user_prompt_templates?.user || '',
                    'User Prompt 模板 - 用户输入',
                    '用于用户直接输入时的提示词模板。可用变量: {mood}, {message}'
                  )}
                >
                  <PenLine className="h-3 w-3 mr-1" />
                  展开
                </Button>
              </div>
              <Textarea
                value={selectedNode.data.user_prompt_templates?.user || ''}
                onChange={(e) => onUpdateNodeData('user_prompt_templates.user', e.target.value)}
                placeholder="可用变量: {mood}, {message}"
                className="text-xs font-mono"
                rows={2}
              />
            </div>

            {/* IoT 数据模板 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-600">IoT 数据 (iot_data)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onOpenPromptEditor(
                    'user_prompt_templates.iot_data',
                    selectedNode.data.user_prompt_templates?.iot_data || '',
                    'User Prompt 模板 - IoT 数据',
                    '用于 IoT 数据触发时的提示词模板。可用变量: {message}, {heart_rate}, {steps} 等IoT数据字段'
                  )}
                >
                  <PenLine className="h-3 w-3 mr-1" />
                  展开
                </Button>
              </div>
              <Textarea
                value={selectedNode.data.user_prompt_templates?.iot_data || ''}
                onChange={(e) => onUpdateNodeData('user_prompt_templates.iot_data', e.target.value)}
                placeholder="可用变量: {message}, {heart_rate}, {steps} 等IoT数据字段"
                className="text-xs font-mono"
                rows={2}
              />
            </div>

            {/* 定时任务模板 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-slate-600">定时任务 (schedule)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onOpenPromptEditor(
                    'user_prompt_templates.schedule',
                    selectedNode.data.user_prompt_templates?.schedule || '',
                    'User Prompt 模板 - 定时任务',
                    '用于定时任务触发时的提示词模板。可用变量: {message}, {trigger_time}, {schedule_name}'
                  )}
                >
                  <PenLine className="h-3 w-3 mr-1" />
                  展开
                </Button>
              </div>
              <Textarea
                value={selectedNode.data.user_prompt_templates?.schedule || ''}
                onChange={(e) => onUpdateNodeData('user_prompt_templates.schedule', e.target.value)}
                placeholder="可用变量: {message}, {trigger_time}, {schedule_name}"
                className="text-xs font-mono"
                rows={2}
              />
            </div>

          </div>
        )}
      </div>
    </>
  )
})

EntryNodeConfig.displayName = 'EntryNodeConfig'